/**
 * Drafts to send (UX round 2026-10-20): every question Merlin drafted for a
 * colleague and nobody has sent or thrown away yet, across all items, from
 * the asks list the shell already polls. A case explorer found no screen
 * that listed them (71 sat on production across 20 items), only a Merlin
 * chat answer whose count changed three times.
 *
 * A question Merlin addressed to two people at once (one broadcast group)
 * is one question here, sent or thrown away as one.
 *
 * Pure logic (repo convention: logic in .ts, components in .tsx).
 */
import { isRoutedToUser } from '../../components/portal/threadTriageView';
import type { DoorDataRequestRead } from '../../services/qcpDataRequests';
import type { DoorInvestigationRead, DoorThreadRead } from '../../services/qcpInvestigations';
import { primaryRef } from './caseIdentity';
import { ORIGIN_LABEL, caseForThread, threadOrigin } from './qualityWorkspaceView';

/** The list endpoint returns the newest 200 asks of every state. */
export const ASK_LIST_LIMIT = 200;

/** One question: the lead draft and any copies addressed to others. */
export interface DraftQuestion {
  key: string;
  lead: DoorDataRequestRead;
  /** The same question's drafts for the other people in its group. */
  others: DoorDataRequestRead[];
  createdAt: string;
}

/** One item (or case, or chat) with its questions waiting. */
export interface DraftItem {
  key: string;
  /** The number the lists show (INV- once it has a case, else QT-). */
  no: string;
  title: string;
  /** Where the item lives now ("Triage", "Routed away", "Done", "Cases"). */
  where: string | null;
  /** Workspace path relative to the door; null when it has no item. */
  path: string | null;
  questions: DraftQuestion[];
  /** The oldest question's draft time: the list leads with what waited longest. */
  oldest: string;
}

function itemKey(r: DoorDataRequestRead): string {
  if (r.thread_id) return `t:${r.thread_id}`;
  if (r.investigation_id) return `c:${r.investigation_id}`;
  return `v:${r.conversation_id ?? 'none'}`;
}

function questionsOf(drafts: DoorDataRequestRead[]): DraftQuestion[] {
  const byGroup = new Map<string, DoorDataRequestRead[]>();
  for (const d of drafts) {
    const key = d.broadcast_group_id ? `g:${d.broadcast_group_id}` : `r:${d.id}`;
    byGroup.set(key, [...(byGroup.get(key) ?? []), d]);
  }
  return [...byGroup.entries()]
    .map(([key, rows]) => {
      const sorted = [...rows].sort((a, b) => a.created_at.localeCompare(b.created_at) || a.id.localeCompare(b.id));
      return { key, lead: sorted[0], others: sorted.slice(1), createdAt: sorted[0].created_at };
    })
    .sort((a, b) => (a.lead.step_no ?? 99) - (b.lead.step_no ?? 99) || a.createdAt.localeCompare(b.createdAt));
}

/** Every unsent draft, grouped by the item it serves, longest-waiting first. */
export function draftItems(
  requests: DoorDataRequestRead[],
  threads: DoorThreadRead[],
  investigations: DoorInvestigationRead[],
  userName: string | null | undefined,
): DraftItem[] {
  const drafts = requests.filter((r) => r.status === 'proposed');
  const byItem = new Map<string, DoorDataRequestRead[]>();
  for (const d of drafts) byItem.set(itemKey(d), [...(byItem.get(itemKey(d)) ?? []), d]);

  const items: DraftItem[] = [...byItem.entries()].map(([key, rows]) => {
    const questions = questionsOf(rows);
    const oldest = questions.reduce((min, q) => (q.createdAt < min ? q.createdAt : min), questions[0].createdAt);
    const first = rows[0];
    const thread = first.thread_id ? threads.find((t) => t.id === first.thread_id) ?? null : null;
    const inv = thread
      ? caseForThread(investigations, thread)
      : first.investigation_id
        ? investigations.find((i) => i.id === first.investigation_id) ?? null
        : null;
    if (thread) {
      const origin = threadOrigin(thread, isRoutedToUser(thread, userName));
      return {
        key,
        no: primaryRef(thread, inv).label,
        title: thread.title,
        where: ORIGIN_LABEL[origin],
        path: `threads/${encodeURIComponent(thread.thread_no)}`,
        questions,
        oldest,
      };
    }
    if (inv) {
      return {
        key,
        no: inv.investigation_no,
        title: inv.title,
        where: inv.status === 'completed' || inv.status === 'cancelled' ? ORIGIN_LABEL.done : ORIGIN_LABEL.cases,
        path: `cases/${encodeURIComponent(inv.investigation_no)}`,
        questions,
        oldest,
      };
    }
    // A thread the door's list does not carry, or a chat with no item.
    return {
      key,
      no: '',
      title: first.thread_id ? 'An item not in your lists' : 'From a Merlin chat, not on an item',
      where: null,
      path: null,
      questions,
      oldest,
    };
  });
  return items.sort((a, b) => a.oldest.localeCompare(b.oldest) || a.no.localeCompare(b.no));
}

/** How many questions wait, over all items. */
export function draftQuestionCount(items: DraftItem[]): number {
  return items.reduce((n, item) => n + item.questions.length, 0);
}

/** "12 questions on 5 items are waiting for you." The count the page and
 *  the More menu both show, so they never disagree. */
export function draftsCountLine(items: DraftItem[]): string {
  const q = draftQuestionCount(items);
  if (q === 0) return 'Nothing Merlin drafted is waiting for you.';
  const items_ = items.length;
  return `${q} question${q === 1 ? '' : 's'} on ${items_} item${items_ === 1 ? '' : 's'} ${q === 1 ? 'is' : 'are'} waiting for you.`;
}

/** True when the list may be missing older drafts (it hit the endpoint's cap). */
export function draftsMayBeCut(requests: DoorDataRequestRead[]): boolean {
  return requests.length >= ASK_LIST_LIMIT;
}

/** "Drafted today" / "Drafted yesterday" / "Drafted 4 days ago". */
export function draftedWhen(iso: string, now: Date = new Date()): string {
  const t = new Date(iso);
  if (Number.isNaN(t.getTime())) return '';
  const day = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const days = Math.round((day(now) - day(t)) / 86_400_000);
  if (days <= 0) return 'Drafted today';
  if (days === 1) return 'Drafted yesterday';
  return `Drafted ${days} days ago`;
}

/** The item numbers in list order, for Previous / Next on the item page. */
export function draftItemOrder(items: DraftItem[]): string[] {
  return items.filter((i) => i.path && i.no).map((i) => i.no);
}
